import React, { useState, useEffect, useMemo, useCallback, useRef, memo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

import {
  Bell,
  Clock,
  Coffee,
  CheckCircle2,
  XCircle,
  Menu as MenuIcon,
  Timer,
  History,
  BarChart3,
  ListFilter,
  CalendarDays,
  BarChart2,
  Search,
  Filter,
  Printer,
  FileDown,
  Share2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { useToast } from '@/components/ui/use-toast';
import { useAuthStore } from '@/stores/authStore';
import TimeInsights from '@/components/TimeInsights';
import { timeTrackingService } from '@/services/timeTrackingService';
import type { TimeTracking, BreakRecord, DailyTimeRecord } from '@/services/timeTrackingService';
import { timeTrackingApiService } from '@/services/timeTrackingApi.service';
import TimeConfirmationDialog from '@/components/TimeConfirmationDialog';
import MissingClockOutDialog from '@/components/MissingClockOutDialog';
import { notificationService, type NotificationItem } from '@/services/notification.service';
import taskService from '@/services/task.service';
import projectService from '@/services/project.service';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { Input } from "@/components/ui/input";

 

// Memoize the NavigationIcons component
interface NavigationIconsProps {
  onMenuClick?: () => void;
}

const NavigationIcons: React.FC<NavigationIconsProps> = ({ onMenuClick }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const userId = user?.id;
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [checkInTime, setCheckInTime] = useState<Date | null>(null);
  const [timeWorked, setTimeWorked] = useState<TimeTracking>({ hours: 0, minutes: 0, seconds: 0 });
  const [lastBreakTime, setLastBreakTime] = useState<Date | null>(null);
  const [isOnBreak, setIsOnBreak] = useState(false);
  const [breakDuration, setBreakDuration] = useState<TimeTracking>({ hours: 0, minutes: 0, seconds: 0 });
  const [totalBreakTime, setTotalBreakTime] = useState<TimeTracking>({ hours: 0, minutes: 0, seconds: 0 });
  const [breakHistory, setBreakHistory] = useState<BreakRecord[]>([]);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmationType, setConfirmationType] = useState<'checkin' | 'checkout' | 'break' | 'endbreak'>('checkin');
  const [requiresReason, setRequiresReason] = useState(false);
  const [reasonLabel, setReasonLabel] = useState('');
  // Initialize date range to today by default
  const getTodayDateString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get current month range (start of month to today)
  const getCurrentMonthRange = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    
    // First day of the month
    const firstDayString = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    
    // Today's date
    const todayString = getTodayDateString();
    
    return { start: firstDayString, end: todayString };
  };

  const [dateRange, setDateRange] = useState(() => {
    const today = getTodayDateString();
    return { 
      start: today, 
      end: today 
    };
  });
  const [_currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState('list');

  // Reset view mode and date range when history dialog opens
  useEffect(() => {
    if (showHistory) {
      // Always start with List view when dialog opens
      setViewMode('list');
      // List view: show only today
      const today = getTodayDateString();
      setDateRange({ start: today, end: today });
    }
  }, [showHistory]);
  
  // Update date range when view mode changes (after initial load)
  useEffect(() => {
    if (!showHistory) return;
    
    if (viewMode === 'calendar') {
      // Calendar view: show from start of month to today
      const monthRange = getCurrentMonthRange();
      setDateRange({ start: monthRange.start, end: monthRange.end });
    } else if (viewMode === 'list') {
      // List view: show only today
      const today = getTodayDateString();
      setDateRange({ start: today, end: today });
    }
  }, [viewMode, showHistory]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    efficiency: { excellent: true, good: true, fair: true, poor: true },
  });
  const [dailyRecords, setDailyRecords] = useState<DailyTimeRecord[]>([]);
  const [_isLoadingSession, setIsLoadingSession] = useState(true);
  
  // Missing clock-out state
  const [hasMissingClockOut, setHasMissingClockOut] = useState(false);
  const [missingClockOutData, setMissingClockOutData] = useState<{
    missingClockOutDate?: string;
    clockInAt?: string;
  } | null>(null);
  const [showMissingClockOutDialog, setShowMissingClockOutDialog] = useState(false);

  // App notifications (from backend)
  const [appNotifications, setAppNotifications] = useState<NotificationItem[]>([]);
  const [notifPage, setNotifPage] = useState(1);
  const [notifHasMore, setNotifHasMore] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  // Track previous user ID to detect user changes
  const prevUserIdRef = useRef<string | undefined>(undefined);

  // Load current session from backend and sync state
  useEffect(() => {
    const loadCurrentSession = async () => {
      // If user changed, clear previous user's state first
      if (prevUserIdRef.current && prevUserIdRef.current !== userId) {
        // Clear previous user's state
        timeTrackingService.clearUserState(prevUserIdRef.current);
        // Reset all state
        setIsCheckedIn(false);
        setCheckInTime(null);
        setLastBreakTime(null);
        setIsOnBreak(false);
        setBreakHistory([]);
        setTotalBreakTime({ hours: 0, minutes: 0, seconds: 0 });
        setTimeWorked({ hours: 0, minutes: 0, seconds: 0 });
        setCurrentSessionId(null);
      }

      // Update previous user ID
      prevUserIdRef.current = userId;

      if (!userId) {
        setIsLoadingSession(false);
        return;
      }

      setIsLoadingSession(true);
      try {
        // First, check for missing clock-out before loading session
        try {
          const missingClockOutCheck = await timeTrackingApiService.checkMissingClockOut();
          if (missingClockOutCheck.hasMissingClockOut) {
            setHasMissingClockOut(true);
            setMissingClockOutData({
              missingClockOutDate: missingClockOutCheck.missingClockOutDate,
              clockInAt: missingClockOutCheck.clockInAt
            });
            setShowMissingClockOutDialog(true);
            setIsLoadingSession(false);
            return; // Block further actions until missing clock-out is corrected
          } else {
            setHasMissingClockOut(false);
            setMissingClockOutData(null);
            setShowMissingClockOutDialog(false);
          }
        } catch (error) {
          console.error('Error checking missing clock-out:', error);
          // Continue with normal flow if check fails
        }
        
        // Fetch current active session from backend
        const currentSession = await timeTrackingApiService.getCurrentSession();
        
        if (currentSession && currentSession.isActive) {
          // Sync state from backend (source of truth)
          // This will properly restore break state if user was on break
          console.log('Loading session from backend:', {
            isActive: currentSession.isActive,
            onBreak: currentSession.onBreak,
            hasBreaks: currentSession.breaks?.length > 0,
            clockInAt: currentSession.clockInAt
          });
          syncStateFromBackend(currentSession);
        } else if (currentSession === null) {
          // Endpoint returned null (might not exist or no session)
          // Try to preserve state from localStorage as fallback
          console.log('No session from backend, checking localStorage for saved state');
          const savedState = timeTrackingService.getCurrentState(userId);
          if (savedState && savedState.userId === userId) {
            // Restore from localStorage (but this might be stale)
            const { 
              isCheckedIn: savedIsCheckedIn, 
              checkInTime: savedCheckInTime,
              lastBreakTime: savedLastBreakTime,
              isOnBreak: savedIsOnBreak,
              breakHistory: savedBreakHistory,
              totalBreakTime: savedTotalBreakTime
            } = savedState;

            setIsCheckedIn(savedIsCheckedIn);
            setCheckInTime(savedCheckInTime ? new Date(savedCheckInTime) : null);
            setLastBreakTime(savedLastBreakTime ? new Date(savedLastBreakTime) : null);
            setIsOnBreak(savedIsOnBreak);
            setBreakHistory(savedBreakHistory?.map((record: any) => ({
              ...record,
              startTime: new Date(record.startTime),
              endTime: record.endTime ? new Date(record.endTime) : undefined
            })) || []);
            setTotalBreakTime(savedTotalBreakTime || { hours: 0, minutes: 0, seconds: 0 });
            
            // If we restored break state, try to sync with backend immediately
            if (savedIsOnBreak && savedLastBreakTime) {
              console.log('Restored break state from localStorage, attempting to sync with backend');
              // Try to refresh from backend in background
              setTimeout(async () => {
                try {
                  const currentSession = await timeTrackingApiService.getCurrentSession();
                  if (currentSession && currentSession.isActive) {
                    syncStateFromBackend(currentSession);
                  }
                } catch (error) {
                  console.error('Error syncing break state from backend:', error);
                }
              }, 1000);
            }
          } else {
            // No saved state - clear everything
            setIsCheckedIn(false);
            setCheckInTime(null);
            setLastBreakTime(null);
            setIsOnBreak(false);
            setBreakHistory([]);
            setTotalBreakTime({ hours: 0, minutes: 0, seconds: 0 });
            setTimeWorked({ hours: 0, minutes: 0, seconds: 0 });
            setCurrentSessionId(null);
          }
        } else {
          // Session exists but is not active (clocked out)
          setIsCheckedIn(false);
          setCheckInTime(null);
          setLastBreakTime(null);
          setIsOnBreak(false);
          setBreakHistory([]);
          setTotalBreakTime({ hours: 0, minutes: 0, seconds: 0 });
          setTimeWorked({ hours: 0, minutes: 0, seconds: 0 });
          setCurrentSessionId(null);
          
          // Clear user-specific localStorage
          timeTrackingService.clearUserState(userId);
        }

        // Load history (user-specific)
        const history = timeTrackingService.getTimeHistory(userId);
        setDailyRecords(history);
      } catch (error) {
        console.error('Error loading current session:', error);
        // On error, still try to load from localStorage as fallback
        const savedState = timeTrackingService.getCurrentState(userId);
        if (savedState && savedState.userId === userId) {
          const { 
            isCheckedIn: savedIsCheckedIn, 
            checkInTime: savedCheckInTime,
            lastBreakTime: savedLastBreakTime,
            isOnBreak: savedIsOnBreak,
            breakHistory: savedBreakHistory,
            totalBreakTime: savedTotalBreakTime
          } = savedState;

          setIsCheckedIn(savedIsCheckedIn);
          setCheckInTime(savedCheckInTime ? new Date(savedCheckInTime) : null);
          setLastBreakTime(savedLastBreakTime ? new Date(savedLastBreakTime) : null);
          setIsOnBreak(savedIsOnBreak);
          setBreakHistory(savedBreakHistory?.map((record: any) => ({
            ...record,
            startTime: new Date(record.startTime),
            endTime: record.endTime ? new Date(record.endTime) : undefined
          })) || []);
          setTotalBreakTime(savedTotalBreakTime || { hours: 0, minutes: 0, seconds: 0 });
        } else {
          // No saved state or belongs to different user - clear everything
          setIsCheckedIn(false);
          setCheckInTime(null);
          setLastBreakTime(null);
          setIsOnBreak(false);
          setBreakHistory([]);
          setTotalBreakTime({ hours: 0, minutes: 0, seconds: 0 });
          setTimeWorked({ hours: 0, minutes: 0, seconds: 0 });
        }
      } finally {
        setIsLoadingSession(false);
      }
    };

    loadCurrentSession();
  }, [userId]); // Reload when user changes

  // Check for missing clock-out on app initialization (if user is already logged in)
  useEffect(() => {
    const checkMissingClockOutOnInit = async () => {
      // Only check if user is available and we haven't already checked
      if (!userId || hasMissingClockOut || showMissingClockOutDialog) return;
      
      // Wait a bit to ensure session loading is complete
      const timeoutId = setTimeout(async () => {
        try {
          const missingClockOutCheck = await timeTrackingApiService.checkMissingClockOut();
          if (missingClockOutCheck.hasMissingClockOut) {
            setHasMissingClockOut(true);
            setMissingClockOutData({
              missingClockOutDate: missingClockOutCheck.missingClockOutDate,
              clockInAt: missingClockOutCheck.clockInAt
            });
            setShowMissingClockOutDialog(true);
          }
        } catch (error) {
          console.error('Error checking missing clock-out on init:', error);
          // Don't block app if check fails
        }
      }, 2000); // Wait 2 seconds after component mount

      return () => clearTimeout(timeoutId);
    };

    checkMissingClockOutOnInit();
  }, [userId]); // Check when userId is available

  // Optimize timer effect with useCallback and useRef
  const timerRef = useRef<NodeJS.Timeout>();
  const lastUpdateRef = useRef<number>(Date.now());

  const updateTime = useCallback(() => {
    const now = new Date();
    if (now.getTime() - lastUpdateRef.current >= 1000) {
      lastUpdateRef.current = now.getTime();
      
      if (isOnBreak && lastBreakTime) {
        const currentBreakMilliseconds = now.getTime() - lastBreakTime.getTime();
        const breakDuration = {
          hours: Math.floor(currentBreakMilliseconds / (1000 * 60 * 60)),
          minutes: Math.floor((currentBreakMilliseconds % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((currentBreakMilliseconds % (1000 * 60)) / 1000)
        };
        setBreakDuration(breakDuration);

        const totalBreakMs = breakHistory.reduce((total, record) => {
          if (record.endTime) {
            return total + (record.endTime.getTime() - record.startTime.getTime());
          }
          return total;
        }, 0) + currentBreakMilliseconds;

        setTotalBreakTime({
          hours: Math.floor(totalBreakMs / (1000 * 60 * 60)),
          minutes: Math.floor((totalBreakMs % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((totalBreakMs % (1000 * 60)) / 1000)
        });
      }

      // Calculate productive time (pauses during breaks)
      // Productive time = total time since clock in - all break time (including current break if on break)
      if (checkInTime) {
        const totalMs = now.getTime() - checkInTime.getTime();
        
        // Calculate all break time
        const completedBreakMs = breakHistory.reduce((total, record) => {
          if (record.endTime) {
            return total + (record.endTime.getTime() - record.startTime.getTime());
          }
          return total;
        }, 0);
        
        // Add current break if on break
        let currentBreakMs = 0;
        if (isOnBreak && lastBreakTime) {
          currentBreakMs = now.getTime() - lastBreakTime.getTime();
        }
        
        const totalBreakMs = completedBreakMs + currentBreakMs;
        
        // Productive time = total time - all break time (pauses during breaks)
        const productiveMs = totalMs - totalBreakMs;
        
        setTimeWorked({
          hours: Math.floor(productiveMs / (1000 * 60 * 60)),
          minutes: Math.floor((productiveMs % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((productiveMs % (1000 * 60)) / 1000)
        });
      } else {
        setTimeWorked({ hours: 0, minutes: 0, seconds: 0 });
      }
    }
  }, [isOnBreak, lastBreakTime, checkInTime, breakHistory]);

  useEffect(() => {
    if (isCheckedIn) {
      timerRef.current = setInterval(updateTime, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isCheckedIn, updateTime]);

  // Helper function to sync state from backend response (defined early for use in other functions)
  const syncStateFromBackend = useCallback((record: any) => {
    console.log('Syncing state from backend:', {
      isActive: record.isActive,
      onBreak: record.onBreak,
      breaks: record.breaks,
      breakDurationMs: record.breakDurationMs
    });

    if (record.clockInAt) {
      setCheckInTime(new Date(record.clockInAt));
      setIsCheckedIn(record.isActive);
      setCurrentSessionId(record._id);
    }
    
    // IMPORTANT: Set break state FIRST before processing breaks
    setIsOnBreak(record.onBreak || false);
    
    // Convert backend break records to frontend format
    let newLastBreakTime: Date | null = null;
    if (record.breaks && record.breaks.length > 0) {
      const convertedBreaks: BreakRecord[] = record.breaks.map((br: any) => ({
        startTime: new Date(br.breakInAt),
        endTime: br.breakOutAt ? new Date(br.breakOutAt) : undefined,
        duration: timeTrackingApiService.msToTimeTracking(br.durationMs || 0),
        reason: br.reason || undefined
      }));
      setBreakHistory(convertedBreaks);
      
      // Set last break time if currently on break
      // The last break in the array should be the current one if onBreak is true
      if (record.onBreak) {
        // Find the active break (one without breakOutAt)
        const activeBreak = record.breaks.find((br: any) => !br.breakOutAt);
        if (activeBreak) {
          // This is the active break - set lastBreakTime to when it started
          newLastBreakTime = new Date(activeBreak.breakInAt);
          setLastBreakTime(newLastBreakTime);
          console.log('✅ Restoring break state - break started at:', newLastBreakTime.toISOString());
          console.log('Break duration so far:', timeTrackingApiService.msToTimeTracking(activeBreak.durationMs || 0));
        } else {
          // Break state says onBreak but no active break found
          // Check if the last break might be the active one
          const lastBreak = record.breaks[record.breaks.length - 1];
          if (lastBreak && !lastBreak.breakOutAt) {
            newLastBreakTime = new Date(lastBreak.breakInAt);
            setLastBreakTime(newLastBreakTime);
            console.log('✅ Restoring break state from last break - break started at:', newLastBreakTime.toISOString());
          } else {
            console.warn('⚠️ Backend says onBreak=true but no active break found in breaks array');
            console.warn('This might be a timing issue. Break state will be preserved but duration may not update until next sync.');
            // Don't clear break state - trust the backend's onBreak flag
            // The break might have just started and not yet in the breaks array
            // Or there might be a timing issue
            // Keep lastBreakTime as null for now - it will be set on next sync
            setLastBreakTime(null);
          }
        }
      } else {
        setLastBreakTime(null);
      }
      
      // Update total break time from backend
      setTotalBreakTime(timeTrackingApiService.msToTimeTracking(record.breakDurationMs || 0));
    } else {
      // No breaks in the record
      setBreakHistory([]);
      
      // If backend says onBreak but no breaks array, this might be a race condition
      // or the break just started. Trust the backend's onBreak flag.
      if (record.onBreak) {
        console.warn('⚠️ Backend says onBreak=true but breaks array is empty');
        console.warn('This might be a race condition. Preserving break state from backend flag.');
        // Keep onBreak as true - the backend knows best
        // We'll need to get the break time from somewhere else or wait for next sync
        // For now, we can't set lastBreakTime without break data, so break duration won't update
        // until the next sync or when break-out happens
        setLastBreakTime(null);
        setTotalBreakTime({ hours: 0, minutes: 0, seconds: 0 });
      } else {
        setTotalBreakTime({ hours: 0, minutes: 0, seconds: 0 });
        setLastBreakTime(null);
      }
    }
    
    // Update productive time from backend
    // Productive time pauses during breaks and resumes after breaks
    // The backend tracks productiveDurationMs which is the productive time up to the last break-out
    // When on break, productive time is paused at the value from backend
    // When not on break, we calculate it in real-time from clock-in minus all breaks
    if (record.clockInAt) {
      if (record.onBreak) {
        // Currently on break - productive time is paused
        // Use productiveDurationMs directly (it's the time before the current break started)
        // This is the accurate value from backend - always trust it when on break
        const productiveTime = timeTrackingApiService.msToTimeTracking(record.productiveDurationMs || 0);
        setTimeWorked(productiveTime);
        console.log('⏸️ On break - productive time paused at:', productiveTime);
      } else {
        // Not on break - calculate current productive time in real-time
        const now = new Date();
        const clockIn = new Date(record.clockInAt);
        const totalMs = now.getTime() - clockIn.getTime();
        const breakMs = record.breakDurationMs || 0;
        // Productive time = total time - all break time
        const productiveMs = totalMs - breakMs;
        setTimeWorked(timeTrackingApiService.msToTimeTracking(Math.max(0, productiveMs)));
      }
    }
  }, []);

  // Function to refresh/sync state from backend
  const refreshSessionFromBackend = useCallback(async () => {
    if (!userId) return false;
    
    try {
      const currentSession = await timeTrackingApiService.getCurrentSession();
      if (currentSession && currentSession.isActive) {
        syncStateFromBackend(currentSession);
        return true;
      }
    } catch (error) {
      console.error('Error refreshing session from backend:', error);
    }
    return false;
  }, [userId, syncStateFromBackend]);

  // Sync with backend periodically and on window focus to prevent state drift
  useEffect(() => {
    if (!userId || !isCheckedIn) return;

    const syncInterval = setInterval(async () => {
      // Sync every 30 seconds to keep state in sync
      await refreshSessionFromBackend();
    }, 30000); // 30 seconds

    // Sync when window gains focus (user comes back to tab)
    const handleFocus = () => {
      refreshSessionFromBackend();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(syncInterval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [userId, isCheckedIn, refreshSessionFromBackend]);

  // Optimize state saving with useCallback and useRef
  const saveTimeoutRef = useRef<NodeJS.Timeout>();
  
  const saveState = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      if (userId) {
        timeTrackingService.saveCurrentState({
          isCheckedIn,
          checkInTime,
          lastBreakTime,
          isOnBreak,
          breakHistory,
          totalBreakTime
        }, userId);
      }
    }, 1000);
  }, [isCheckedIn, checkInTime, lastBreakTime, isOnBreak, breakHistory, totalBreakTime, userId]);

  useEffect(() => {
    saveState();
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [saveState]);


  // Handle missing clock-out correction
  const handleCorrectMissingClockOut = async (clockOutTime: string, reason: string) => {
    try {
      await timeTrackingApiService.correctMissingClockOut({
        clockOutTime,
        reason
      });
      
      // Clear missing clock-out state
      setHasMissingClockOut(false);
      setMissingClockOutData(null);
      setShowMissingClockOutDialog(false);
      
      // Reload session after correction
      const loadCurrentSession = async () => {
        if (!userId) return;
        
        try {
          const currentSession = await timeTrackingApiService.getCurrentSession();
          if (currentSession && currentSession.isActive) {
            syncStateFromBackend(currentSession);
          }
        } catch (error) {
          console.error('Error loading session after correction:', error);
        }
      };
      
      await loadCurrentSession();
      
      toast({
        title: "✅ Clock-Out Corrected",
        description: "Your missing clock-out has been recorded. You can now continue.",
        className: "bg-gradient-to-r from-green-50 to-emerald-50 border-green-200",
      });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to correct missing clock-out');
    }
  };

  const handleCheckInOutClick = () => {
    // Block clock-in if there's a missing clock-out
    if (!isCheckedIn && hasMissingClockOut) {
      toast({
        title: "⚠️ Missing Clock-Out",
        description: "Please correct your missing clock-out before clocking in.",
        variant: "destructive",
      });
      setShowMissingClockOutDialog(true);
      return;
    }
    
    const now = new Date();
    const isCheckIn = !isCheckedIn;
    
    if (isCheckIn) {
      // Check if clock-in time is >= 10:00 AM
      const hours = now.getHours();
      const needsReason = hours >= 10;
      setRequiresReason(needsReason);
      setReasonLabel(needsReason ? 'Please provide a reason for late check-in' : '');
    } else {
      // Check if total hours < 9h or productive hours < 8h
      // Total hours = work time + break time
      const workHours = timeWorked.hours + (timeWorked.minutes / 60) + (timeWorked.seconds / 3600);
      const breakHours = totalBreakTime.hours + (totalBreakTime.minutes / 60) + (totalBreakTime.seconds / 3600);
      const totalHours = workHours + breakHours;
      const productiveHours = workHours; // Productive time is work time (excluding breaks)
      const needsReason = totalHours < 9 || productiveHours < 8;
      setRequiresReason(needsReason);
      setReasonLabel(needsReason ? 'Please provide a reason for early check-out' : '');
    }
    
    setConfirmationType(isCheckIn ? 'checkin' : 'checkout');
    setShowConfirmDialog(true);
  };

  const handleBreakClick = async () => {
    // Before showing break dialog, sync with backend to ensure state is accurate
    // This prevents issues where frontend and backend are out of sync
    if (!isOnBreak) {
      // About to start break - check backend first
      const synced = await refreshSessionFromBackend();
      if (synced && isOnBreak) {
        // Backend says we're already on break - update UI and show break-out instead
        toast({
          title: "Break Status Updated",
          description: "Your break status has been synced with the server.",
          className: "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200",
        });
        // Now show break-out dialog since we're on break
        const breakHours = breakDuration.hours + (breakDuration.minutes / 60) + (breakDuration.seconds / 3600);
        const needsReason = breakHours > 1;
        setRequiresReason(needsReason);
        setReasonLabel(needsReason ? 'Please provide a reason for extended break' : '');
        setConfirmationType('endbreak');
        setShowConfirmDialog(true);
        return;
      }
    }
    
    const isBreakIn = !isOnBreak;
    
    if (!isBreakIn) {
      // Check if break duration > 1 hour
      const breakHours = breakDuration.hours + (breakDuration.minutes / 60) + (breakDuration.seconds / 3600);
      const needsReason = breakHours > 1;
      setRequiresReason(needsReason);
      setReasonLabel(needsReason ? 'Please provide a reason for extended break' : '');
    } else {
      setRequiresReason(false);
      setReasonLabel('');
    }
    
    setConfirmationType(isBreakIn ? 'break' : 'endbreak');
    setShowConfirmDialog(true);
  };

  const handleConfirmAction = (reason?: string) => {
    switch (confirmationType) {
      case 'checkin':
      case 'checkout':
        handleConfirmCheckInOut(reason);
        break;
      case 'break':
      case 'endbreak':
        handleConfirmBreak(reason);
        break;
    }
  };

  const handleConfirmBreak = async (reason?: string) => {
    try {
      let record;
      if (!isOnBreak) {
        // Break In - check backend first to avoid "already have active break" error
        try {
          record = await timeTrackingApiService.breakIn();
          toast({
            title: "☕ Break Started",
            description: "Your break time has started. Take your time!",
            className: "bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200",
          });
        } catch (breakInError: any) {
          // If backend says we already have an active break, sync state from backend
          if (breakInError.response?.status === 400 && 
              breakInError.response?.data?.message?.includes('already have an active break')) {
            console.log('Backend says break is already active - syncing state');
            // Refresh session from backend to get current state
            const synced = await refreshSessionFromBackend();
            if (synced) {
              toast({
                title: "Break Status Synced",
                description: "Your break was already active. Status has been updated.",
                className: "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200",
              });
              setShowConfirmDialog(false);
              return;
            }
          }
          throw breakInError;
        }
      } else {
        // Break Out
        record = await timeTrackingApiService.breakOut(reason);
        const breakTime = timeTrackingService.formatTime(breakDuration);
        toast({
          title: "💪 Break Ended",
          description: `Break duration: ${breakTime}`,
          className: "bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200",
        });
        setBreakDuration({ hours: 0, minutes: 0, seconds: 0 });
      }
      
      // Sync state from backend response
      syncStateFromBackend(record);
      setShowConfirmDialog(false);
    } catch (error: any) {
      console.error('Error handling break:', error);
      
      // If it's the "already have active break" error, try to sync state
      if (error.response?.status === 400 && 
          error.response?.data?.message?.includes('already have an active break')) {
        const synced = await refreshSessionFromBackend();
        if (synced) {
          toast({
            title: "Break Status Synced",
            description: "Your break was already active. Status has been updated.",
            className: "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200",
          });
          setShowConfirmDialog(false);
          return;
        }
      }
      
      toast({
        title: "Error",
        description: error.response?.data?.message || error.message || "Failed to update break status",
        variant: "destructive",
      });
    }
  };

  const handleConfirmCheckInOut = async (reason?: string) => {
    try {
      if (!isCheckedIn) {
        // Clock In
        const record = await timeTrackingApiService.clockIn(reason);
        syncStateFromBackend(record);
        toast({
          title: "✅ Welcome!",
          description: "You have successfully checked in. Have a productive day!",
          className: "bg-gradient-to-r from-green-50 to-emerald-50 border-green-200",
        });
      } else {
        // Clock Out - end break first if on break
        if (isOnBreak) {
          try {
            const breakRecord = await timeTrackingApiService.breakOut();
            syncStateFromBackend(breakRecord);
          } catch (error) {
            console.error('Error ending break before clock out:', error);
          }
        }
        
        const record = await timeTrackingApiService.clockOut(reason);
        
        // Calculate final times from backend response
        const finalTime = timeTrackingApiService.msToTimeTracking(record.totalDurationMs);
        const finalBreakTime = timeTrackingApiService.msToTimeTracking(record.breakDurationMs);
        
        toast({
          title: "👋 Day Summary",
          description: `Work time: ${timeTrackingService.formatTime(finalTime)}\nTotal breaks: ${timeTrackingService.formatTime(finalBreakTime)}`,
          className: "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200",
        });
        
        // Reset all state
        setCheckInTime(null);
        setLastBreakTime(null);
        setTimeWorked({ hours: 0, minutes: 0, seconds: 0 });
        setBreakDuration({ hours: 0, minutes: 0, seconds: 0 });
        setBreakHistory([]);
        setTotalBreakTime({ hours: 0, minutes: 0, seconds: 0 });
        setIsCheckedIn(false);
        setIsOnBreak(false);
        setCurrentSessionId(null);
      }
      setShowConfirmDialog(false);
    } catch (error: any) {
      console.error('Error handling check in/out:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update check-in/out status",
        variant: "destructive",
      });
    }
  };


  
  const effectiveUnread = unreadCount || 0;

  // Fetch counts periodically and refresh list when count changes
  useEffect(() => {
    let mounted = true;
    let lastCount = -1;
    let intervalId: number | undefined;

    const fetchCounts = async () => {
      try {
        const counts = await notificationService.getCounts();
        if (!mounted) return;
        setUnreadCount(counts.unreadCount);
        // If count changed (especially increased), refresh the list in background
        if (lastCount !== counts.unreadCount) {
          lastCount = counts.unreadCount;
          // Refresh notifications silently to keep the dropdown fresh
          loadNotifications(1);
        }
      } catch (error) {
        console.error('Failed to fetch notification counts:', error);
      }
    };

    // Initial immediate load: counts and list
    fetchCounts();
    loadNotifications(1);

    const startPolling = () => {
      // Poll every 10s while tab is visible to make updates feel immediate
      intervalId = window.setInterval(fetchCounts, 10000);
    };
    const stopPolling = () => {
      if (intervalId) {
        window.clearInterval(intervalId);
        intervalId = undefined;
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchCounts();
        startPolling();
      } else {
        stopPolling();
      }
    };

    // Respect page visibility to avoid unnecessary background polling
    document.addEventListener('visibilitychange', handleVisibility);
    startPolling();

    return () => {
      mounted = false;
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load notifications when dropdown opens (always refresh to get latest)
  useEffect(() => {
    if (notificationsOpen) {
      loadNotifications(1, true); // Refresh both notifications and count
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationsOpen]);

  const loadNotifications = async (page = 1, refreshCount = false) => {
    setNotifLoading(true);
    try {
      const res = await notificationService.getNotifications({ page, limit: 10 });
      setAppNotifications(prev => page === 1 ? res.data : [...prev, ...res.data]);
      setNotifHasMore(res.hasMore);
      setNotifPage(res.page);
      // Refresh counts if requested
      if (refreshCount) {
        const counts = await notificationService.getCounts();
        setUnreadCount(counts.unreadCount);
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
      toast({
        title: 'Error',
        description: 'Failed to load notifications. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setNotifLoading(false);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationService.markAsRead([]);
      setAppNotifications(prev => prev.map(n => ({ ...n, isRead: true, readAt: n.readAt || new Date().toISOString() })));
      setUnreadCount(0);
      toast({
        title: 'Success',
        description: 'All notifications marked as read',
      });
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      toast({
        title: 'Error',
        description: 'Failed to mark notifications as read',
        variant: 'destructive',
      });
    }
  };

  const markOneAsRead = async (id: string) => {
    try {
      await notificationService.markAsRead([id]);
      setAppNotifications(prev => prev.map(n => n._id === id ? { ...n, isRead: true, readAt: n.readAt || new Date().toISOString() } : n));
      setUnreadCount(c => Math.max(0, c - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleNotificationClick = async (n: NotificationItem) => {
    // Mark read optimistically
    markOneAsRead(n._id);
    if (n.referenceModel === 'Project' && n.referenceId) {
      try {
        // Fetch the latest project and team members so Team section renders immediately
        const [project, rawTeam] = await Promise.all([
          projectService.getProjectById(n.referenceId),
          projectService.getProjectTeamMembers(n.referenceId).catch(() => [])
        ]);
        // Enrich team members with names if only IDs are provided
        let team: any[] = Array.isArray(rawTeam) ? rawTeam : [];
        if (team.length > 0 && team[0] && !team[0].firstName) {
          try {
            const membersWithNames = await Promise.all(team.map(async (m: any) => {
              const empId = m.employeeId || m.employee?.id || m.employee?._id || m._id || m.id;
              if (!empId) return null;
              try {
                const emp = await (await import('@/services/employee.service')).getEmployeeById(empId);
                const firstName = emp?.user?.firstName || emp?.firstName || '';
                const lastName = emp?.user?.lastName || emp?.lastName || '';
                return { _id: emp?.user?._id || emp?._id || empId, firstName, lastName };
              } catch {
                return null;
              }
            }));
            team = membersWithNames.filter(Boolean) as any[];
          } catch {}
        }
        const enriched = { ...project, team } as any;
        navigate(`/projects/${n.referenceId}`, { state: { project: enriched } });
        return;
      } catch (error) {
        console.error('Failed to fetch project/team for notification:', error);
        navigate(`/projects/${n.referenceId}`);
        return;
      }
    }
    // Handle Task notifications or task comment mentions
    // Check for Task referenceModel, Comment referenceModel (for task comments), or task mention in title
    const isTaskRelated = 
      n.referenceModel === 'Task' || 
      n.referenceModel === 'Comment' ||
      n.title?.toLowerCase().includes('task comment') ||
      n.title?.toLowerCase().includes('mentioned in a task') ||
      n.type === 'task_mention';
    
    if (isTaskRelated) {
      // Try to get task ID from multiple sources
      let taskId = n.referenceId;
      if (!taskId && n.metadata) {
        taskId = n.metadata.taskId || n.metadata.referenceId || n.metadata.task?._id || n.metadata.task?.id;
      }
      
      if (taskId) {
        try {
          console.log('Opening task notification for taskId:', taskId);
          const task = await taskService.getTaskById(taskId);
          console.log('Fetched task:', task);
          // Extract projectId - handle both string and object types for project
          let projectId = task?.projectId;
          if (!projectId && task?.project) {
            if (typeof task.project === 'string') {
              projectId = task.project;
            } else if (typeof task.project === 'object' && task.project !== null) {
              projectId = (task.project as any).id || (task.project as any)._id;
            }
          }
          console.log('Extracted projectId:', projectId);
          if (projectId) {
            // Use replace: false to ensure state is preserved and component re-renders
            navigate(`/projects/${projectId}`, { state: { openTaskId: taskId }, replace: false });
            return;
          }
        } catch (error) {
          console.error('Failed to handle task notification:', error);
          // Fallback: try to navigate using metadata if available
          if (n.metadata?.projectId) {
            navigate(`/projects/${n.metadata.projectId}`, { state: { openTaskId: taskId }, replace: false });
            return;
          }
        }
      }
    }
  };

  // Load time history data from API when dialog opens or date range changes
  useEffect(() => {
    const loadTimeHistory = async () => {
      if (!showHistory || !dateRange.start || !dateRange.end) return;
      
      console.log('Loading time history for date range:', dateRange);
      
      try {
        const summary = await timeTrackingApiService.getDailySummary(dateRange.start, dateRange.end);
        console.log('API response:', summary);
        
        // Convert API response to DailyTimeRecord format
        const convertedRecords: DailyTimeRecord[] = summary.days.map(day => {
          const totalTime = timeTrackingApiService.msToTimeTracking(day.totalDurationMs);
          const productiveTime = timeTrackingApiService.msToTimeTracking(day.productiveDurationMs);
          const breakTime = timeTrackingApiService.msToTimeTracking(day.breakDurationMs);
          
          // Calculate efficiency
          const totalMinutes = totalTime.hours * 60 + totalTime.minutes;
          const productiveMinutes = productiveTime.hours * 60 + productiveTime.minutes;
          const efficiency = totalMinutes > 0 ? Math.round((productiveMinutes / totalMinutes) * 100) : 0;
          
          return {
            date: day.date.split('T')[0],
            checkInTime: day.clockInAt 
              ? new Date(day.clockInAt).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true
                })
              : '',
            checkOutTime: day.clockOutAt
              ? new Date(day.clockOutAt).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true
                })
              : undefined,
            totalWorkTime: totalTime,
            productiveTime: productiveTime,
            totalBreakTime: breakTime,
            breaks: [], // Break details not in summary, would need separate API call
            efficiency
          };
        });
        
        console.log('Converted records:', convertedRecords);
        setDailyRecords(convertedRecords);
      } catch (error) {
        console.error('Error loading time history:', error);
        // Fallback to localStorage if API fails
        if (userId) {
          const history = timeTrackingService.getTimeHistory(userId);
          setDailyRecords(history);
        }
      }
    };

    loadTimeHistory();
  }, [showHistory, dateRange.start, dateRange.end, userId]);

  // Update the filteredAndSortedRecords to use dailyRecords and filter by date range
  const filteredAndSortedRecords = useMemo(() => {
    let filtered = dailyRecords.filter(record => {
      // Filter by date range
      const recordDate = record.date;
      const matchesDateRange = 
        (!dateRange.start || recordDate >= dateRange.start) &&
        (!dateRange.end || recordDate <= dateRange.end);

      const matchesSearch = searchTerm === '' || 
        record.date.toLowerCase().includes(searchTerm.toLowerCase());

      // Check if any efficiency filter is selected
      const hasAnyFilter = 
        filters.efficiency.excellent || 
        filters.efficiency.good || 
        filters.efficiency.fair || 
        filters.efficiency.poor;
      
      // If no filters are selected, show all records. Otherwise, filter by selected efficiency ranges.
      const matchesEfficiency = !hasAnyFilter || 
        (record.efficiency >= 80 && filters.efficiency.excellent) ||
        (record.efficiency >= 60 && record.efficiency < 80 && filters.efficiency.good) ||
        (record.efficiency >= 40 && record.efficiency < 60 && filters.efficiency.fair) ||
        (record.efficiency < 40 && filters.efficiency.poor);

      return matchesDateRange && matchesSearch && matchesEfficiency;
    });

    return filtered.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateB - dateA;
    });
  }, [dailyRecords, dateRange.start, dateRange.end, searchTerm, filters]);

  const getTimeDistributionData = () => {
    const data = [
      { value: 40, color: '#0088FE' },
      { value: 30, color: '#00C49F' },
      { value: 30, color: '#FFBB28' },
    ];
    return data;
  };

  const convertToMinutes = (time: TimeTracking): number => {
    return time.hours * 60 + time.minutes;
  };

  const exportToCSV = () => {
    // Implementation of exportToCSV function
  };

  return (
    <nav className="bg-white/90 backdrop-blur-xl border-b border-gray-200/80 sticky top-0 z-50 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.05)]">
      <div className="w-full">
        <div className="flex h-16 items-center px-4 sm:px-6">
          {/* Left Section - Logo */}
                      <div className="flex-shrink-0">
              <Link to="/" className="block group">
                <img 
                  src="/logo-header.svg" 
                  alt="Dvij Infotech Logo" 
                  className="h-8 sm:h-12 w-auto transition-transform duration-300 group-hover:scale-105"
                  onError={(e) => {
                    // Fallback to PNG if SVG fails
                    const target = e.target as HTMLImageElement;
                    target.src = '/logo192.png';
                  }}
                />
              </Link>
            </div>

          {/* Right Section - Combined Controls */}
          <div className="flex items-center gap-4 ml-auto">
            {/* Analytics & History Buttons */}
            <div className="hidden md:flex items-center gap-2">
              <Button
                onClick={() => setShowAnalytics(true)}
                variant="ghost"
                className="h-9 px-2 sm:px-4 text-gray-600 hover:text-gray-900 hover:bg-gray-50/80 flex items-center gap-1 sm:gap-2 font-medium transition-all duration-200 rounded-xl hover:shadow-sm"
              >
                <BarChart3 className="h-[18px] w-[18px] stroke-[2.25px] text-blue-600" />
                <span className="text-xs sm:text-sm">Analytics</span>
              </Button>
              <Button
                onClick={() => setShowHistory(true)}
                variant="ghost"
                className="h-9 px-2 sm:px-4 text-gray-600 hover:text-gray-900 hover:bg-gray-50/80 flex items-center gap-1 sm:gap-2 font-medium transition-all duration-200 rounded-xl hover:shadow-sm"
              >
                <History className="h-[18px] w-[18px] stroke-[2.25px] text-indigo-600" />
                <span className="text-xs sm:text-sm">History</span>
              </Button>
            </div>

            {/* Time Controls */}
            {!isCheckedIn ? (
              <Button
                onClick={handleCheckInOutClick}
                variant="outline"
                className="h-9 px-2 sm:px-4 bg-gradient-to-r from-emerald-500/10 via-emerald-500/10 to-green-500/10 border-emerald-200 text-emerald-700 hover:bg-emerald-50/80 flex items-center gap-1 sm:gap-2 shadow-sm transition-all duration-200 rounded-xl hover:shadow-md hover:scale-[1.02]"
              >
                <CheckCircle2 className="h-[18px] w-[18px] stroke-[2.25px]" />
                <span className="font-medium text-xs sm:text-sm">Check In</span>
                <span className="text-xs text-emerald-600 font-normal hidden sm:inline">
                  Start your day
                </span>
              </Button>
            ) : (
              <div className="flex items-center gap-1 sm:gap-2">
                <Button
                  onClick={handleBreakClick}
                  variant="outline"
                  className={`h-9 px-2 sm:px-4 flex items-center gap-1 sm:gap-2 shadow-sm transition-all duration-200 rounded-xl hover:shadow-md hover:scale-[1.02] ${
                    isOnBreak
                      ? 'bg-gradient-to-r from-amber-500/10 via-amber-500/10 to-orange-500/10 border-amber-200 text-amber-700 hover:bg-amber-50/80'
                      : 'bg-gradient-to-r from-purple-500/10 via-purple-500/10 to-fuchsia-500/10 border-purple-200 text-purple-700 hover:bg-purple-50/80'
                  }`}
                >
                  <Coffee className="h-[18px] w-[18px] stroke-[2.25px]" />
                  <span className="font-medium text-xs sm:text-sm">{isOnBreak ? 'End Break' : 'Break'}</span>
                  {isOnBreak && (
                    <span className="text-xs font-normal tabular-nums hidden sm:inline">
                      {timeTrackingService.formatTime(breakDuration)}
                    </span>
                  )}
                </Button>

                <Button
                  onClick={handleCheckInOutClick}
                  variant="outline"
                  className="h-9 px-2 sm:px-4 bg-gradient-to-r from-red-500/10 via-red-500/10 to-rose-500/10 border-red-200 text-red-700 hover:bg-red-50/80 flex items-center gap-1 sm:gap-2 shadow-sm transition-all duration-200 rounded-xl hover:shadow-md hover:scale-[1.02]"
                >
                  <XCircle className="h-[18px] w-[18px] stroke-[2.25px]" />
                  <span className="font-medium text-xs sm:text-sm">Check Out</span>
                  <span className="text-xs text-red-600 font-normal tabular-nums hidden sm:inline">
                    {timeTrackingService.formatTime(timeWorked)}
                  </span>
                </Button>
              </div>
            )}

            {/* Notifications */}
            <DropdownMenu open={notificationsOpen} onOpenChange={setNotificationsOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => { try { setNotificationsOpen(true); loadNotifications(1, true); } catch {} }}
                  className="relative h-9 w-9 hover:bg-gray-50/80 text-gray-500 hover:text-gray-700 transition-all duration-200 rounded-xl hover:shadow-sm"
                >
                  <Bell className="h-[18px] w-[18px] stroke-[2.25px]" />
                  {effectiveUnread > 0 && (
                    <Badge
                      className="absolute -top-1 -right-1 h-4 w-4 flex items-center justify-center bg-red-500 text-[10px] p-0 border-2 border-white animate-pulse"
                      variant="destructive"
                    >
                      {effectiveUnread > 99 ? '99+' : effectiveUnread}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-80 max-h-[400px] flex flex-col">
                <DropdownMenuLabel className="font-normal shrink-0">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">Notifications</p>
                    <div className="flex items-center justify-between">
                      <p className="text-xs leading-none text-muted-foreground">Your latest updates</p>
                      <button onClick={markAllAsRead} className="text-xs text-blue-600 hover:underline">Mark all read</button>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="shrink-0" />
                <DropdownMenuGroup className="overflow-y-auto max-h-[300px]">
                  {notifLoading && appNotifications.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-500">Loading…</div>
                  ) : appNotifications.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-500">
                      No notifications
                    </div>
                  ) :
                    appNotifications.map((n) => (
                      <DropdownMenuItem key={n._id} className="p-3 cursor-pointer hover:bg-gray-50/80 transition-colors" onClick={() => handleNotificationClick(n)}>
                        <div className="flex items-start gap-3 w-full">
                          {/* Simple icon by type */}
                          <Bell className={`h-5 w-5 ${n.isRead ? 'text-gray-400' : 'text-blue-500'} shrink-0 mt-0.5`} />
                          <div className="space-y-1 flex-1">
                            <p className={`text-sm font-medium leading-none ${n.isRead ? 'text-gray-600' : 'text-gray-900'}`}>{n.title}</p>
                            <p className="text-xs text-gray-500 line-clamp-2">{n.message}</p>
                            <p className="text-[10px] text-gray-400">{new Date(n.createdAt).toLocaleString()}</p>
                          </div>
                          {!n.isRead && (
                            <button onClick={(e) => { e.stopPropagation(); markOneAsRead(n._id); }} className="text-xs text-blue-600 hover:underline">Mark</button>
                          )}
                        </div>
                      </DropdownMenuItem>
                    ))
                  }
                </DropdownMenuGroup>
                {notifHasMore && (
                  <div className="p-2 shrink-0 border-t">
                    <Button variant="ghost" className="w-full text-sm" onClick={() => loadNotifications(notifPage + 1)} disabled={notifLoading}>
                      {notifLoading ? 'Loading…' : 'Load more'}
                    </Button>
                  </div>
                )}
                {appNotifications.length > 0 && (
                  <div className="p-2 border-t shrink-0">
                    <Button 
                      variant="ghost" 
                      className="w-full text-sm" 
                      onClick={() => {
                        loadNotifications(1, true);
                      }}
                      disabled={notifLoading}
                    >
                      {notifLoading ? 'Refreshing…' : 'Refresh'}
                    </Button>
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Menu */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onMenuClick}
              className="lg:hidden h-9 w-9 hover:bg-gray-50/80 text-gray-500 hover:text-gray-700 transition-all duration-200 rounded-xl hover:shadow-sm"
            >
              <MenuIcon className="h-[18px] w-[18px] stroke-[2.25px]" />
            </Button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {/* Missing Clock-Out Dialog - Mandatory */}
      <MissingClockOutDialog
        isOpen={showMissingClockOutDialog}
        missingClockOutDate={missingClockOutData?.missingClockOutDate}
        clockInAt={missingClockOutData?.clockInAt}
        onSubmit={handleCorrectMissingClockOut}
      />

      <TimeConfirmationDialog
        isOpen={showConfirmDialog}
        onClose={() => {
          setShowConfirmDialog(false);
          setRequiresReason(false);
          setReasonLabel('');
        }}
        onConfirm={handleConfirmAction}
        type={confirmationType}
        timeWorked={timeWorked}
        breakDuration={breakDuration}
        totalBreakTime={totalBreakTime}
        requiresReason={requiresReason}
        reasonLabel={reasonLabel}
      />

      {/* Analytics/History Dialog */}
      <Dialog open={showAnalytics || showHistory} onOpenChange={(open) => {
        if (showHistory) {
          setShowHistory(open);
          // Reset view mode and date range when dialog closes
          if (!open) {
            setViewMode('list');
            const today = getTodayDateString();
            setDateRange({ start: today, end: today });
          }
        } else {
          setShowAnalytics(open);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-gray-900">
              {showHistory ? 'Time History' : ''}
            </DialogTitle>
          </DialogHeader>
          
          {showHistory ? (
            <div className="flex-1 overflow-y-auto">
              {/* Toolbar */}
              <div className="border-b px-6 py-3 bg-gray-50/50">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 flex-1">
                    <Input
                      type="date"
                      value={dateRange.start}
                      onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                      className="w-36"
                    />
                    <span className="text-gray-500">to</span>
                    <Input
                      type="date"
                      value={dateRange.end}
                      onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                      className="w-36"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`gap-2 ${viewMode === 'list' ? 'bg-gray-100' : ''}`}
                      onClick={() => {
                        setViewMode('list');
                        // List view: show only today
                        const today = getTodayDateString();
                        setDateRange({ start: today, end: today });
                      }}
                    >
                      <ListFilter className="h-4 w-4" />
                      List
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`gap-2 ${viewMode === 'calendar' ? 'bg-gray-100' : ''}`}
                      onClick={() => {
                        setViewMode('calendar');
                        // Calendar view: show from start of month to today
                        const monthRange = getCurrentMonthRange();
                        setDateRange({ start: monthRange.start, end: monthRange.end });
                      }}
                    >
                      <CalendarDays className="h-4 w-4" />
                      Calendar
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`gap-2 ${viewMode === 'analytics' ? 'bg-gray-100' : ''}`}
                      onClick={() => setViewMode('analytics')}
                    >
                      <BarChart2 className="h-4 w-4" />
                      Analytics
                    </Button>
                  </div>
                </div>

                <div className="flex items-center gap-3 mt-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search records..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon">
                        <Filter className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel>Efficiency</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuCheckboxItem
                        checked={filters.efficiency.excellent}
                        onCheckedChange={(checked) => 
                          setFilters(prev => ({
                            ...prev,
                            efficiency: { ...prev.efficiency, excellent: checked }
                          }))
                        }
                      >
                        Excellent (80%+)
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={filters.efficiency.good}
                        onCheckedChange={(checked) => 
                          setFilters(prev => ({
                            ...prev,
                            efficiency: { ...prev.efficiency, good: checked }
                          }))
                        }
                      >
                        Good (60-79%)
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={filters.efficiency.fair}
                        onCheckedChange={(checked) => 
                          setFilters(prev => ({
                            ...prev,
                            efficiency: { ...prev.efficiency, fair: checked }
                          }))
                        }
                      >
                        Fair (40-59%)
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={filters.efficiency.poor}
                        onCheckedChange={(checked) => 
                          setFilters(prev => ({
                            ...prev,
                            efficiency: { ...prev.efficiency, poor: checked }
                          }))
                        }
                      >
                        Poor (≤ 40%)
                      </DropdownMenuCheckboxItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button variant="outline" size="icon" onClick={() => window.print()}>
                    <Printer className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="icon" onClick={exportToCSV}>
                    <FileDown className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="icon">
                    <Share2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {viewMode === 'list' && (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[200px]">Date</TableHead>
                          <TableHead className="w-[180px]">Check In / Out</TableHead>
                          <TableHead className="w-[120px]">Productive Time</TableHead>
                          <TableHead className="w-[120px]">Break Time</TableHead>
                          <TableHead className="w-[150px]">Efficiency</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredAndSortedRecords.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                              No records found for the selected date range.
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredAndSortedRecords.map((record, index) => (
                            <TableRow key={index} className="hover:bg-gray-50/50">
                              <TableCell className="font-medium">
                                <div className="flex items-center gap-2">
                                  <span>
                                    {new Date(record.date).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    })}
                                  </span>
                                  {new Date(record.date).toDateString() === new Date().toDateString() && (
                                    <Badge variant="secondary" className="text-xs">Today</Badge>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  {new Date(record.date).toLocaleDateString('en-US', {
                                    weekday: 'long'
                                  })}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2 text-sm">
                                  <Clock className="h-4 w-4 text-gray-400" />
                                  <span className="text-gray-700">
                                    {record.checkInTime || 'N/A'} 
                                    {record.checkOutTime ? ` - ${record.checkOutTime}` : ' - Present'}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Timer className="h-4 w-4 text-blue-500" />
                                  <span className="font-medium text-blue-600">
                                    {timeTrackingService.formatTime(record.productiveTime || { hours: 0, minutes: 0, seconds: 0 })}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Coffee className="h-4 w-4 text-purple-500" />
                                  <span className="font-medium text-purple-600">
                                    {timeTrackingService.formatTime(record.totalBreakTime)}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-3">
                                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden min-w-[80px]">
                                    <div 
                                      className={`h-full rounded-full transition-all duration-500 ${
                                        record.efficiency >= 80 ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
                                        record.efficiency >= 60 ? 'bg-gradient-to-r from-blue-500 to-indigo-500' :
                                        record.efficiency >= 40 ? 'bg-gradient-to-r from-amber-500 to-orange-500' :
                                        'bg-gradient-to-r from-red-500 to-rose-500'
                                      }`}
                                      style={{ width: `${record.efficiency}%` }}
                                    />
                                  </div>
                                  <span className={`text-sm font-medium whitespace-nowrap ${
                                    record.efficiency >= 80 ? 'text-green-600' :
                                    record.efficiency >= 60 ? 'text-blue-600' :
                                    record.efficiency >= 40 ? 'text-amber-600' :
                                    'text-red-600'
                                  }`}>
                                    {record.efficiency}%
                                  </span>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {viewMode === 'calendar' && (() => {
                  // Get the first day of the month
                  const startDate = new Date(dateRange.start + 'T00:00:00');
                  const firstDayOfMonth = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
                  const dayOfWeek = firstDayOfMonth.getDay(); // 0 = Sunday, 1 = Monday, etc.
                  
                  // Get today's date
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  
                  // Get the last day of the month
                  const lastDayOfMonth = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);
                  const daysInMonth = lastDayOfMonth.getDate();
                  
                  // Calculate days from previous month to show
                  const prevMonth = new Date(startDate.getFullYear(), startDate.getMonth() - 1, 0);
                  const daysInPrevMonth = prevMonth.getDate();
                  
                  // Calculate total cells needed (previous month days + current month days + next month days)
                  const totalCells = dayOfWeek + daysInMonth;
                  const rowsNeeded = Math.ceil(totalCells / 7);
                  const cellsToRender = rowsNeeded * 7;

                  return (
                    <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="bg-gray-50 p-2 text-center text-sm font-medium text-gray-500">
                          {day}
                        </div>
                      ))}
                      {Array.from({ length: cellsToRender }).map((_, index) => {
                        let date: Date;
                        let dayNumber: number;
                        let isPrevMonth = false;
                        let isNextMonth = false;
                        
                        // Days from previous month
                        if (index < dayOfWeek) {
                          dayNumber = daysInPrevMonth - dayOfWeek + index + 1;
                          date = new Date(startDate.getFullYear(), startDate.getMonth() - 1, dayNumber);
                          isPrevMonth = true;
                        }
                        // Days from current month
                        else if (index < dayOfWeek + daysInMonth) {
                          dayNumber = index - dayOfWeek + 1;
                          date = new Date(startDate.getFullYear(), startDate.getMonth(), dayNumber);
                        }
                        // Days from next month
                        else {
                          dayNumber = index - dayOfWeek - daysInMonth + 1;
                          date = new Date(startDate.getFullYear(), startDate.getMonth() + 1, dayNumber);
                          isNextMonth = true;
                        }
                        
                        date.setHours(0, 0, 0, 0);
                        // Format date as YYYY-MM-DD without timezone conversion
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        const dateString = `${year}-${month}-${day}`;
                        const record = dailyRecords.find(r => r.date === dateString);
                        
                        const isToday = date.getTime() === today.getTime();
                        const isFuture = date > today;
                        const isOtherMonth = isPrevMonth || isNextMonth;

                        // Future dates or other month dates: just show the date number with white background
                        if (isFuture || isOtherMonth) {
                          return (
                            <div 
                              key={index}
                              className="p-2 min-h-[100px] bg-white"
                            >
                              <div className={`text-sm ${isOtherMonth ? 'text-gray-300' : 'text-gray-400'}`}>
                                {dayNumber}
                              </div>
                            </div>
                          );
                        }

                        // Past and today dates: show with background and data
                        return (
                          <div 
                            key={index}
                            className={`bg-white p-2 min-h-[100px] ${
                              record ? 'hover:bg-gray-50/50' : 'bg-gray-50/50'
                            } ${isToday ? 'ring-2 ring-blue-500' : ''} transition-colors`}
                          >
                            <div className={`text-sm ${isToday ? 'font-semibold text-blue-600' : 'text-gray-400'}`}>
                              {dayNumber}
                            </div>
                            {(() => {
                              // If no record exists for this date, show "Absent" with 00:00:00
                              if (!record) {
                                return (
                                  <div className="mt-2">
                                    <div className="text-xs font-medium text-gray-500">
                                      Absent
                                    </div>
                                    <div className="flex items-center gap-1 mt-1">
                                      <Timer className="h-3 w-3 text-gray-400" />
                                      <span className="text-xs text-gray-500">
                                        00:00:00
                                      </span>
                                    </div>
                                  </div>
                                );
                              }
                              
                              // Check if there's actual time data (not 00:00:00)
                              const hasTimeData = record.totalWorkTime.hours > 0 || 
                                                  record.totalWorkTime.minutes > 0 || 
                                                  record.totalWorkTime.seconds > 0 ||
                                                  (record.checkInTime !== '' && record.checkInTime !== undefined);
                              
                              // Show "Absent" if no time data, otherwise show time tracking info
                              if (!hasTimeData) {
                                return (
                                  <div className="mt-2">
                                    <div className="text-xs font-medium text-gray-500">
                                      Absent
                                    </div>
                                    <div className="flex items-center gap-1 mt-1">
                                      <Timer className="h-3 w-3 text-gray-400" />
                                      <span className="text-xs text-gray-500">
                                        00:00:00
                                      </span>
                                    </div>
                                  </div>
                              );
                              }
                              
                              return (
                                <div className="mt-2 space-y-1">
                                  <div className="text-xs font-medium text-gray-900">
                                    {record.checkInTime} - {record.checkOutTime || 'Present'}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Timer className="h-3 w-3 text-blue-500" />
                                    <span className="text-xs text-blue-600">
                                      {timeTrackingService.formatTime(record.totalWorkTime)}
                                    </span>
                                  </div>
                                  <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                                    <div 
                                      className={`h-full rounded-full ${
                                        record.efficiency >= 80 ? 'bg-green-500' :
                                        record.efficiency >= 60 ? 'bg-blue-500' :
                                        record.efficiency >= 40 ? 'bg-amber-500' :
                                        'bg-red-500'
                                      }`}
                                      style={{ width: `${record.efficiency}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {viewMode === 'analytics' && (
                  <div className="space-y-6">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Efficiency Trend</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="h-[300px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={filteredAndSortedRecords}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                              <XAxis 
                                dataKey="date" 
                                tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric' 
                                })}
                              />
                              <YAxis />
                              <Tooltip />
                              <Bar 
                                dataKey="efficiency" 
                                fill="#8b5cf6"
                                radius={[4, 4, 0, 0]}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>

                    <div className="grid grid-cols-2 gap-6">
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base">Work vs Break Time</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="h-[300px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={getTimeDistributionData()}
                                  cx="50%"
                                  cy="50%"
                                  innerRadius={60}
                                  outerRadius={80}
                                  paddingAngle={5}
                                  dataKey="value"
                                >
                                  {getTimeDistributionData().map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                  ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base">Daily Work Hours</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="h-[300px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={filteredAndSortedRecords}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis 
                                  dataKey="date" 
                                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { 
                                    month: 'short', 
                                    day: 'numeric' 
                                  })}
                                />
                                <YAxis />
                                <Tooltip />
                                <Bar 
                                  dataKey={(record) => convertToMinutes(record.totalWorkTime) / 60}
                                  fill="#60a5fa"
                                  radius={[4, 4, 0, 0]}
                                />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto pr-2">
              <TimeInsights
                timeWorked={timeWorked}
                totalBreakTime={totalBreakTime}
                breakHistory={breakHistory}
                defaultTab={showHistory ? 'daily' : 'current'}
                showAllTabs={!showHistory}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </nav>
  );
};

export default memo(NavigationIcons); 